/* eslint-disable react/jsx-indent */
import axios from 'axios';
import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { blur, reduxForm, Field, change } from 'redux-form';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import { DefaultButton, ReduxTextField, ReduxSelectField, MoneyCell, SliderField } from 'common/components';
import { required, numericality } from 'redux-form-validators';
import { FormMessageHOC } from 'common/HOCs';
import {
  ALLOCATION_METHOD_BY_EQUITY,
  ALLOCATION_METHOD_FIXED,
  ALLOCATION_PERCENT_MAX,
  ALLOCATION_PERCENT_MIN,
  ALLOCATION_PERCENT_STEP,
  ALLOCATION_PERCENT_DEFAULT,
} from '../../consts';

import { addStrategyInvestments, getMamMetaInvestExternalAccounts } from '../../_redux';
import { percentToRatio } from '../../helpers';

import { staticStyles } from './style';

const InvestStrategyForm = ({
  strategyId,
  strategyAccountId,
  strategyCurrency,
  minInvestmentAmount,
  accounts,
  walletAccounts,
  walletBalance,
  isWallet,
  isMultiWallet,
  isRollover,
  isMamMetaInvest,
  getMamMetaInvestExternalAccounts,
  mamMetaInvestExternalAccounts,
  tpslInRollovers,
  afterSubmitSuccess,
  handleSubmit,
  submitting,
  pristine,
  invalid,
  intl,
  error,
  dispatch,
}) => {
  const [selectedAccountAmount, setSelectedAccountAmount] = useState(0);
  const [selectedAccountCurrency, setSelectedAccountCurrency] = useState('');
  const [takeProfitFilled, setTakeProfitFilled] = useState(false);
  const [stopLossFilled, setStopLossFilled] = useState(false);
  const [exchangeCoefficient, setExchangeCoefficient] = useState(1);
  const [amountToInvest, setAmountToInvest] = useState(0);
  const [investInfo, setInvestInfo] = useState({});
  const [receiveAmount, setReceiveAmount] = useState(0);
  const { details, availableAmount } = investInfo;
  const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));
  const { recipientCurrencyDigit = 2 } = useMemo(() => {
    if (details) {
      const { recipientCurrencyDigit } = details;
      return { recipientCurrencyDigit };
    }
    return {};
  }, [details]);

  useEffect(() => {
    if (isWallet) {
      axios
        .get('/internal_transfers/transfer/invest_from_wallet/', {
          params: { recipientAccount: strategyAccountId },
        })
        .then(({ data }) => {
          setExchangeCoefficient(data?.details?.exchangeCoefficient || 1);
          setInvestInfo(data);
        });
    }
  }, []);

  useEffect(() => {
    if (isMamMetaInvest) {
      getMamMetaInvestExternalAccounts(strategyAccountId);
    }
  }, [getMamMetaInvestExternalAccounts]);

  const calculateReceiveAmount = value => {
    const sendAmount = parseFloat(value);
    if (typeof sendAmount === 'number' && sendAmount <= availableAmount && details) {
      const { exchangeCoefficient = 1, commission } = details;
      let calculatedCommission =
        (sendAmount / 100) * commission.percentage + commission.fix * details.exchangeCoefficientForFix;
      if (calculatedCommission > details.maxCommission.amount) calculatedCommission = details.maxCommission.amount;
      if (calculatedCommission < details.minCommission.amount) calculatedCommission = details.minCommission.amount;
      const receiveAmount = (sendAmount - calculatedCommission) * exchangeCoefficient;
      let roundedReceiveAmount = 0;
      if (receiveAmount > 0) {
        roundedReceiveAmount = getFixedNumber(receiveAmount, recipientCurrencyDigit);
      }
      setReceiveAmount(roundedReceiveAmount.toString());
    } else {
      setReceiveAmount(null);
    }
  };

  useEffect(() => {
    calculateReceiveAmount(amountToInvest);
  }, [details]);

  const submit = handleSubmit(values => {
    const takeProfitAndStopLoss = {
      takeProfit: { amount: values.takeProfit, currency: strategyCurrency },
      stopLoss: { amount: values.stopLoss, currency: strategyCurrency },
    };

    if (isMamMetaInvest) {
      const { externalAccount, ratio, allocationMethod } = values;

      return addStrategyInvestments(
        {
          externalAccount,
          strategy: strategyId,
          ratio: ratio ? percentToRatio(ratio) : ratio,
          allocationMethod,
          ...(values.takeProfit !== undefined
            ? { takeProfit: { amount: values.takeProfit, currency: strategyCurrency } }
            : {}),
          ...(values.stopLoss !== undefined
            ? { stopLoss: { amount: values.stopLoss, currency: strategyCurrency } }
            : {}),
        },
        dispatch
      );
    }

    const { ratio, ...investmentValues } = values;

    return addStrategyInvestments(
      {
        ...investmentValues,
        money: { amount: values.money, currency: isWallet ? 'USD' : selectedAccountCurrency },
        strategy: strategyId,
        ...(values.takeProfit !== undefined ? takeProfitAndStopLoss : {}),
      },
      dispatch
    );
  });

  const handleChangeAccount = value => {
    const selectedAccount = isMultiWallet
      ? walletAccounts.find(account => account.id === value)
      : accounts.find(account => account.id === value);
    setSelectedAccountAmount(selectedAccount.balance.amount);
    setSelectedAccountCurrency(selectedAccount.balance.currency);

    const url = isMultiWallet
      ? '/internal_transfers/transfer/invest_from_wallet_account/'
      : '/internal_transfers/transfer/invest/';

    axios
      .get(url, {
        params: { senderAccount: selectedAccount.id, recipientAccount: strategyAccountId },
      })
      .then(({ data }) => {
        setExchangeCoefficient(data?.details?.exchangeCoefficient || 1);
        setInvestInfo(data);
      });
  };

  const handleChangeTakeProfit = value => setTakeProfitFilled(value !== '');
  const handleChangeStopLoss = value => setStopLossFilled(value !== '');

  const withdrawalAccountEnabled = takeProfitFilled && stopLossFilled;

  useEffect(() => {
    if (!withdrawalAccountEnabled) dispatch(change('InvestStrategyForm', 'conditionsAccount', null));
  }, [withdrawalAccountEnabled]);

  const accountOptions = accounts.map(({ id, login, balance }) => ({
    value: id,
    label: `${login} (${balance.amount} ${balance.currency})`,
  }));

  const walletAccountOptions = walletAccounts.map(({ id, login, balance }) => ({
    value: id,
    label: `${login} (${balance.amount} ${balance.currency})`,
  }));

  const mamMetaInvestExternalAccountsOptions = mamMetaInvestExternalAccounts.map(({ id, login, balance }) => ({
    value: id,
    label: `${login} (${balance.amount} ${balance.currency})`,
  }));

  const allocationMethodOptions = [
    { label: intl.formatMessage({ id: 'justByEquity' }), value: ALLOCATION_METHOD_BY_EQUITY },
    { label: intl.formatMessage({ id: 'justFixed' }), value: ALLOCATION_METHOD_FIXED },
  ];

  const handleRatioBlur = (event, value) => {
    if (value === '') {
      return;
    }

    const normalizedValue = Math.min(ALLOCATION_PERCENT_MAX, Math.max(ALLOCATION_PERCENT_MIN, Number(value)));

    if (normalizedValue !== Number(value)) {
      event.preventDefault();
      dispatch(blur('InvestStrategyForm', 'ratio', normalizedValue));
    }
  };

  const justAmountOfInvestmentNew =
    document.location.hostname === 'my.tixee.com' ||
    document.location.hostname === 'portal.disona.com' ||
    document.location.hostname === 'portal.rock-west.com'
      ? 'justAmountOfInvestmentTixee'
      : 'justAmountOfInvestment';

  const justTpSlWithdrawalAccountDescriptionNew =
    document.location.hostname === 'my.tixee.com' ||
    document.location.hostname === 'portal.disona.com' ||
    document.location.hostname === 'portal.rock-west.com'
      ? 'justTpSlWithdrawalAccountDescriptionTixee'
      : 'justTpSlWithdrawalAccountDescription';

  const investAvailableFundsTipNew =
    document.location.hostname === 'my.tixee.com' ||
    document.location.hostname === 'portal.disona.com' ||
    document.location.hostname === 'portal.rock-west.com' ||
    document.location.hostname === 'portal.motforex.com'
      ? 'investAvailableFundsTipTixee'
      : 'investAvailableFundsTip';
  const justMinimumInvestAmountNewOld =
    document.location.hostname === 'my.tixee.com' ||
    document.location.hostname === 'portal.disona.com' ||
    document.location.hostname === 'portal.rock-west.com'
      ? 'justMinimumInvestAmountTixee'
      : 'justMinimumInvestAmount';

  const justMinimumInvestAmountNew =
    document.location.hostname === 'portal.motforex.com' ? 'justMinimumInvestAmountMot' : justMinimumInvestAmountNewOld;

  const takeProfitRequiredValidation = () => {
    if (isMamMetaInvest) {
      return [];
    }

    if (stopLossFilled) {
      return [required()];
    }

    return [];
  };

  const stopLossRequiredValidation = () => {
    if (isMamMetaInvest) {
      return [];
    }

    if (takeProfitFilled) {
      return [required()];
    }

    return [];
  };

  return (
    <form className="InvestStrategyForm" onSubmit={submit}>
      {isMamMetaInvest && (
        <Field
          component={ReduxSelectField}
          name="externalAccount"
          maxMenuHeight={140}
          textId="clientMetaExternalAccount"
          options={mamMetaInvestExternalAccountsOptions}
          disabled={submitting}
          validate={[required()]}
          searchable
        />
      )}
      {!isWallet && !isMamMetaInvest && (
        <Field
          component={ReduxSelectField}
          name="account"
          maxMenuHeight={140}
          textId={isMultiWallet ? 'justWalletTitle' : 'justAccount'}
          options={isMultiWallet ? walletAccountOptions : accountOptions}
          onChange={handleChangeAccount}
          disabled={submitting}
          validate={[required()]}
          searchable
        />
      )}
      {!isMamMetaInvest && (
        <Field
          component={ReduxTextField}
          textId={justAmountOfInvestmentNew}
          type="number"
          name="money"
          disabled={submitting}
          validate={[required(), numericality({ '>=': 0, '<=': isWallet ? walletBalance : selectedAccountAmount })]}
          description={isWallet ? 'USD' : selectedAccountCurrency}
          onChange={newValue => {
            setAmountToInvest(Number(newValue));
            calculateReceiveAmount(Number(newValue));
          }}
          tooltipId={investAvailableFundsTipNew}
          tooltipTextId={investAvailableFundsTipNew}
        />
      )}
      {isWallet && !isMamMetaInvest && (
        <div className="InvestStrategyForm__desc">
          <FormattedMessage id="rammAccountAmount" />
          <MoneyCell value={{ amount: '100', currency: 'USD' }} />
        </div>
      )}
      {typeof amountToInvest === 'number' && amountToInvest !== 0 && (
        <div className="InvestStrategyForm__desc">
          <FormattedMessage
            id={
              document.location.pathname === 'portal.motforex.com'
                ? 'clinetYouWillInvestAroundMot'
                : 'clinetYouWillInvestAround'
            }
          />
          <MoneyCell
            value={{ amount: amountToInvest * exchangeCoefficient, currency: strategyCurrency }}
            maxDigitsAfterDecimal={recipientCurrencyDigit}
          />
        </div>
      )}
      {receiveAmount && amountToInvest ? (
        <div className="InvestStrategyForm__desc">
          <FormattedMessage id="justWillReceive" />
          <MoneyCell value={{ amount: receiveAmount, currency: strategyCurrency }} />
        </div>
      ) : null}
      {!isMamMetaInvest && (
        <div className="InvestStrategyForm__desc">
          <FormattedMessage id={justMinimumInvestAmountNew} />
          <MoneyCell value={{ amount: minInvestmentAmount, currency: strategyCurrency }} />
        </div>
      )}
      {(!isRollover || tpslInRollovers) && (
        <>
          <div className="InvestStrategyForm__line">
            <Field
              component={ReduxTextField}
              textId="justTakeProfit"
              type="number"
              name="takeProfit"
              onChange={handleChangeTakeProfit}
              description={strategyCurrency}
              disabled={submitting}
              validate={[...takeProfitRequiredValidation()]}
            />

            <Field
              component={ReduxTextField}
              textId="justStopLoss"
              type="number"
              name="stopLoss"
              onChange={handleChangeStopLoss}
              description={strategyCurrency}
              disabled={submitting}
              validate={[...stopLossRequiredValidation()]}
            />
          </div>
          {isMamMetaInvest && (
            <>
              <Field
                component={ReduxTextField}
                textId="justPercent"
                type="number"
                name="ratio"
                validate={[
                  required(),
                  numericality({
                    '>=': ALLOCATION_PERCENT_MIN,
                    '<=': ALLOCATION_PERCENT_MAX,
                    allowBlank: true,
                  }),
                ]}
                disabled={submitting}
                description="%"
                onBlur={handleRatioBlur}
                shouldUseOnlyFormValue
              />
              <Field
                component={SliderField}
                name="ratio"
                minValue={ALLOCATION_PERCENT_MIN}
                maxValue={ALLOCATION_PERCENT_MAX}
                step={ALLOCATION_PERCENT_STEP}
                unit="%"
                disabled={submitting}
              />
              <div className="InvestStrategyForm__allocation-caption">
                <FormattedMessage id="investAllocationDescription" />
              </div>
              <Field
                component={ReduxSelectField}
                name="allocationMethod"
                textId="justAllocationMethod"
                options={allocationMethodOptions}
                disabled={submitting}
                validate={[required()]}
              />
            </>
          )}
          {!isMamMetaInvest && (
            <>
              <Field
                component={ReduxSelectField}
                name="conditionsAccount"
                maxMenuHeight={140}
                textId="justTpSlWithdrawalAccount"
                options={accountOptions}
                disabled={!withdrawalAccountEnabled || submitting}
                validate={withdrawalAccountEnabled ? [required()] : []}
                searchable
              />
              <div className="InvestStrategyForm__desc">
                <FormattedMessage id={justTpSlWithdrawalAccountDescriptionNew} />
              </div>
            </>
          )}
        </>
      )}
      {isRollover && (
        <p className="InvestStrategyForm__desc">
          <FormattedMessage id="investRolloversOrderWarning" />
        </p>
      )}
      {error && <strong className="formError">{error}</strong>}
      <div className="InvestStrategyForm__line">
        <DefaultButton
          type="submit"
          textId="justInvest"
          loading={submitting}
          disabled={invalid || submitting || pristine}
          filled
        />
        <DefaultButton textId="justCancel" onClick={() => afterSubmitSuccess()} />
      </div>
      <style jsx>{staticStyles}</style>
    </form>
  );
};

InvestStrategyForm.propTypes = {
  strategyAccountId: PropTypes.number.isRequired,
  minInvestmentAmount: PropTypes.string.isRequired,
  accounts: PropTypes.array.isRequired,
  walletAccounts: PropTypes.array,
  afterSubmitSuccess: PropTypes.func.isRequired,
  isWallet: PropTypes.bool.isRequired,
  strategyCurrency: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  strategyId: PropTypes.number,
  walletBalance: PropTypes.string,
  error: PropTypes.string,
  isRollover: PropTypes.bool.isRequired,
  tpslInRollovers: PropTypes.bool.isRequired,
  intl: PropTypes.object.isRequired,
  isMultiWallet: PropTypes.bool,
  isMamMetaInvest: PropTypes.bool,
  mamMetaInvestExternalAccounts: PropTypes.array,
  getMamMetaInvestExternalAccounts: PropTypes.func.isRequired,
};

InvestStrategyForm.defaultProps = {
  isMultiWallet: false,
  isMamMetaInvest: false,
  walletAccounts: [],
  mamMetaInvestExternalAccounts: [],
  walletBalance: 0,
  strategyId: null,
  error: '',
};

export default compose(
  injectIntl,
  FormMessageHOC,
  reduxForm({
    form: 'InvestStrategyForm',
    initialValues: { ratio: ALLOCATION_PERCENT_DEFAULT },
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage('requestSentSuccessfully');
      props.afterSubmitSuccess();
    },
  }),
  connect(
    state => ({
      mamMetaInvestExternalAccounts: state.client.invest.mamMetaInvestExternalAccounts,
    }),
    {
      getMamMetaInvestExternalAccounts: accountId => getMamMetaInvestExternalAccounts.request({ accountId }),
    }
  )
)(InvestStrategyForm);
export { InvestStrategyForm };
